import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Droplets, Building2, MapPin, Users, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { analyticsAPI } from '../../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MetricCard = ({ icon: Icon, title, value, subtitle, trend, status }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardContent className="p-3 sm:p-4 md:p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground text-xs sm:text-sm mb-1 sm:mb-2">
            <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="truncate">{title}</span>
          </div>
          <div className="text-lg sm:text-2xl md:text-3xl font-bold text-foreground mb-0.5 sm:mb-1">{value}</div>
          {subtitle && <div className="text-[10px] sm:text-xs md:text-sm text-muted-foreground truncate">{subtitle}</div>}
        </div>
        {status && (
          <Badge 
            variant={status === 'good' ? 'default' : status === 'warning' ? 'outline' : 'destructive'}
            className={`${status === 'good' ? 'bg-success text-success-foreground' : ''} text-[10px] sm:text-xs ml-1`}
          >
            {trend}
          </Badge>
        )}
      </div>
    </CardContent>
  </Card>
);

export const Overview = ({ villageData = {} }) => {
  const [overviewData, setOverviewData] = useState(null);
  const [supplyDemandData, setSupplyDemandData] = useState([]);
  const [nrwDistributionData, setNrwDistributionData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadAnalyticsData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch overview KPIs
      const overviewResponse = await fetch(`${API_URL}/api/analytics/overview`);
      const overview = await overviewResponse.json();
      setOverviewData(overview);

      // Fetch supply-demand data
      const supplyDemandResponse = await fetch(`${API_URL}/api/analytics/supply-demand`);
      const supplyDemand = await supplyDemandResponse.json();
      setSupplyDemandData(supplyDemand.data || []);

      // Fetch NRW distribution
      const nrwDistResponse = await fetch(`${API_URL}/api/analytics/nrw-distribution`);
      const nrwDist = await nrwDistResponse.json();
      
      const colors = [
        'hsl(var(--chart-5))', // Red
        'hsl(var(--chart-4))', // Orange
        'hsl(var(--chart-1))', // Blue
        'hsl(var(--chart-2))', // Teal
        'hsl(var(--chart-3))', // Green
      ];
      
      const formattedNRW = (nrwDist.data || []).map((item, idx) => ({
        name: item.name,
        value: item.percentage,
        nrwPercent: item.nrw,
        color: colors[idx % colors.length]
      }));
      setNrwDistributionData(formattedNRW);

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalyticsData();
  }, [villageData, loadAnalyticsData]);

  // Convert villageData object to array and add status/alerts
  const villagesArray = Object.values(villageData).map(village => ({
    ...village,
    alerts: village.nrw > 15 ? 8 : village.nrw > 12 ? 5 : village.nrw > 10 ? 2 : 1,
    status: village.nrw > 15 ? 'critical' : village.nrw > 12 ? 'warning' : 'good'
  }));

  if (isLoading || !overviewData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-1 sm:mb-2">Village Overview</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Real-time monitoring of water supply infrastructure for Salcette Block, Goa</p>
      </div>
      
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <MetricCard
          icon={Building2}
          title="Total WTPs"
          value={overviewData.total_wtps}
          subtitle={`${overviewData.wtps_maintenance} under maintenance`}
          status="good"
          trend="Active"
        />
        <MetricCard
          icon={MapPin}
          title="Villages Covered"
          value={overviewData.villages_covered}
          subtitle="Salcette Block"
          status="good"
          trend="100%"
        />
        <MetricCard
          icon={Users}
          title="Total Households"
          value={(overviewData.total_households ?? 0).toLocaleString()}
          subtitle={`${overviewData.avg_lpcd ?? 0} LPCD avg (Target: ${overviewData.target_lpcd ?? 0} LPCD)`}
        />
        <MetricCard
          icon={TrendingDown}
          title="Village NRW"
          value={`${overviewData.village_nrw}%`}
          subtitle={`Target: ${overviewData.nrw_target}%`}
          status={overviewData.village_nrw > 12 ? "warning" : "good"}
          trend={`+${overviewData.nrw_change}%`}
        />
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Supply vs Demand Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Supply vs Demand Trend (MLD)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={supplyDemandData}>
                <defs>
                  <linearGradient id="colorSupply" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorDemand" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Area type="monotone" dataKey="supply" stroke="hsl(var(--success))" fill="url(#colorSupply)" name="Supply" strokeWidth={2} />
                <Area type="monotone" dataKey="demand" stroke="hsl(var(--destructive))" fill="url(#colorDemand)" name="Demand" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        {/* NRW Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>NRW Loss Distribution by Village</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={nrwDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, value }) => `${name}: ${value}%`}
                  outerRadius={100}
                  fill="hsl(var(--chart-1))"
                  dataKey="value"
                >
                  {nrwDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name, entry) => [
                    `${value}% of total NRW (${entry.payload?.nrwPercent ?? 0}% NRW rate, ${(entry.payload?.households ?? 0).toLocaleString()} households)`,
                    entry.payload?.name ?? 'Unknown'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground text-center">
                Distribution shows each village's contribution to total block NRW
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {nrwDistributionData.map((village, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-xs">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: village.color }}
                    />
                    <span>{village.name} ({village.nrwPercent}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Village-wise Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Village-wise Infrastructure Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Village</th>
                  <th className="text-center">WTPs</th>
                  <th className="text-center">OHTs</th>
                  <th className="text-center">DMAs</th>
                  <th className="text-center">Households</th>
                  <th className="text-center">NRW %</th>
                  <th className="text-center">Alerts</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {villagesArray.length > 0 ? (
                  villagesArray.map((village, idx) => (
                    <tr key={idx}>
                      <td className="font-medium">{village.name}</td>
                      <td className="text-center">{village.wtps}</td>
                      <td className="text-center">{village.ohts}</td>
                      <td className="text-center">{village.dmas}</td>
                      <td className="text-center">{(village.households ?? 0).toLocaleString()}</td>
                      <td className="text-center">
                        <span className={`font-semibold ${
                          village.nrw < 10 ? 'text-success' : 
                          village.nrw < 15 ? 'text-warning' : 'text-destructive'
                        }`}>
                          {village.nrw}%
                        </span>
                      </td>
                      <td className="text-center">
                        {village.alerts > 0 ? (
                          <Badge variant="destructive" className="text-xs">{village.alerts}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Badge 
                          variant={village.status === 'good' ? 'default' : 'outline'}
                          className={
                            village.status === 'good' ? 'bg-success text-success-foreground' :
                            village.status === 'warning' ? 'bg-warning text-warning-foreground' :
                            'bg-destructive text-destructive-foreground'
                          }
                        >
                          {village.status === 'good' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                          {village.status === 'warning' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {village.status === 'critical' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {village.status.charAt(0).toUpperCase() + village.status.slice(1)}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted-foreground py-8">
                      No village data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-muted-foreground">Supply Efficiency</h3>
              <Badge variant="outline" className="bg-success/10 text-success border-success/20">Good</Badge>
            </div>
            <div className="text-2xl font-bold mb-2">93.8%</div>
            <Progress value={93.8} className="h-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-muted-foreground">Quality Compliance</h3>
              <Badge variant="outline" className="bg-success/10 text-success border-success/20">Excellent</Badge>
            </div>
            <div className="text-2xl font-bold mb-2">96.2%</div>
            <Progress value={96.2} className="h-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-muted-foreground">Active Alerts</h3>
              <Badge variant="destructive">5 Critical</Badge>
            </div>
            <div className="text-2xl font-bold mb-2">20 Total</div>
            <Progress value={25} className="h-2" indicatorClassName="bg-destructive" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Overview;